'use client';

import { FC, useState } from 'react';
import { css } from '@bts-web/utils-style-engine';
import { DatadogErrorLevels } from '@bts-web/utils-next-infrastructure';
import { useClientLocale, useClientTranslation } from '@bts-web/utils-lokalise';
import { getAppConfig } from '@bts-web/core-app-config';
import {
  intlFiatValueFormatting,
  PrecisionFloat,
  timeScalarFromDateUtc,
} from '@bts-web/utils-formatting';
import { convertToLocaleDateString } from '@bts-web/utils-formatting-client';
import {
  AmountInputWrapper,
  BelowInputInfoTable,
  ButtonBase,
  valueFromAssetToFiat,
} from '../../../common';
import { datadogErrorHelper } from '../../../common/utils/datadogErrorHelper';
import { useHandleGqlErrorsWithSnackbar } from '../../../common/utils/errors';
import { createUnstakeAction } from '../../../common/gqlActions';
import { TradeAssetInfoDisplay } from '../../../common/components/TradeAssetInfoDisplay/TradeAssetInfoDisplay';
import { FilledSpinnerIos } from '../../../common/svgIcons';
import { PoweredByTextWithLogo } from '../../../trade/Reusable/PoweredBy/PoweredBy';
import { StakeTranslations } from '../../utils/getStakeTranslations';
import {
  finishTradeRedirectBuilder,
  IFinishTradeRedirectBuilderProps,
} from '../../../trade/FinishTrade/actions/finishTradeRedirectBuilder.action';
import { UnstakeAssetAcceptViewQuery$data } from '@bts-web/data-layer/server';
import {
  ConversionSettings,
  getConvertedSummary,
} from '../../../trade/CreateTrade/utils/getConvertedSummary';
import { MutableNonNullableAsset } from '../../../trade/CreateTrade/types';
import { AssetType } from '../../../asset-details/types';

const executeRedirect = (props: IFinishTradeRedirectBuilderProps) => {
  finishTradeRedirectBuilder(props);
};

export type AcceptUnstakeSummaryProps = {
  translations: StakeTranslations;
  assetId: string;
  amount: string;
  unstakeAsset: NonNullable<
    NonNullable<UnstakeAssetAcceptViewQuery$data['unstakeAsset']>['items']
  >[number];
  currentUnstaked?: PrecisionFloat;
};

export const AcceptUnstakeSummary: FC<AcceptUnstakeSummaryProps> = ({
  translations,
  assetId,
  unstakeAsset,
  amount,
  currentUnstaked,
}) => {
  const { t } = useClientTranslation();

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const locale = useClientLocale();

  const [isConfirmLoading, setIsConfirmLoading] = useState(false);

  const [isConfirmDone, setIsConfirmDone] = useState(false);

  const { asset, stakedFiat } = unstakeAsset;

  const { logoUrl, name, price, symbol, staking, portfolio } = asset;

  const { type, unboundedPeriodInDays } = staking || {};

  const currentUnstakingAmount = valueFromAssetToFiat(amount, price);

  const fundsAvailableDate = new Date();

  fundsAvailableDate.setDate(
    fundsAvailableDate.getDate() + (unboundedPeriodInDays || 0),
  );

  const fundsAvailableAt = convertToLocaleDateString(
    timeScalarFromDateUtc(fundsAvailableDate),
    locale,
  );

  const availableAmount =
    Number(portfolio?.fiatBalance?.value || 0) -
    Number(unstakeAsset?.stakedFiat?.value || '0') -
    Number(currentUnstaked?.value || '0');

  const { currency } = getAppConfig();

  const triggerAcceptUnstake = async () => {
    setIsConfirmLoading(true);

    try {
      const response = await createUnstakeAction({
        assetId,
        amount,
      });

      if (response.errors) {
        setIsConfirmLoading(false);

        setIsConfirmDone(false);

        executeRedirect({
          assetId,
          transactionStatus: 'FAILED',
          offerId: 'accept_unstake', // in case of staking fails we redirecting to /accept/FAILED we do not have stakeId
          variant: 'unstake',
        });

        return;
      }

      if (!response.data || !response.data.unstake) {
        throw new Error('server failure: missing response data');
      }

      executeRedirect({
        assetId,
        transactionStatus: 'SUCCESS',
        offerId: response.data.unstake.id,
        variant: 'unstake',
      });
    } catch (error) {
      setIsConfirmLoading(false);

      setIsConfirmDone(false);

      processErrors(
        typeof error === 'string'
          ? [error]
          : typeof error === 'object' &&
              error &&
              'errors' in error &&
              Array.isArray((error as any).errors)
            ? (error as any).errors
            : [],
      );

      datadogErrorHelper({
        errorMessage:
          error instanceof Error ? error.message : JSON.stringify(error),
        errorSeverity: DatadogErrorLevels.CRITICAL,
      });
    }
  };

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        px: 'medium',
        height: '100%',
        gap: 'medium',
      })}
    >
      <div>
        <TradeAssetInfoDisplay
          sourceAssetData={{ logoUrl, name } as MutableNonNullableAsset}
          symbol={symbol as string}
          operation="unstake"
        />

        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: 'medium',
          })}
        >
          <AmountInputWrapper
            inputContextData={{
              symbol: symbol as string,
              currency,
              displayType: 'ASSET',
              value: amount,
              placeholder: undefined,
            }}
          />

          <BelowInputInfoTable
            items={[
              {
                value: getConvertedSummary({
                  assetPrice: price,
                  tradeType: 'stake',
                  assetType: AssetType.CRYPTO,
                  currency,
                  displayType: 'ASSET',
                  locale,
                  symbol: symbol as string,
                  translations: {
                    afterFees: translations.afterFees,
                  } as ConversionSettings['translations'],
                  value: amount,
                }),
              },
            ]}
          />

          {type === 'LOCKED' && (
            <div
              className={css({
                borderWidth: '1px',
                borderColor: 'warning.stroke_primary',
                color: 'warning.text_primary',
                padding: 'small',
                borderEndEndRadius: 'smallBottomRight',
                borderEndStartRadius: 'smallTopRight',
                borderStartStartRadius: 'smallTopLeft',
                borderStartEndRadius: 'smallBottomLeft',
              })}
            >
              {t('bonded_unstake_warning', {
                days: unboundedPeriodInDays,
              })}
            </div>
          )}

          <div
            className={css({
              pt: 'extra_small',
              pb: 'medium',
              borderColor: 'neutrals.divider',
              borderTopWidth: '1px',
            })}
          >
            <BelowInputInfoTable
              items={[
                {
                  title: translations.youUnstaking,
                  value: intlFiatValueFormatting(
                    currentUnstakingAmount?.value || 0,
                    {
                      locale,
                      currency,
                      fractionDigits: currentUnstakingAmount?.precision,
                    },
                  ),
                },
                {
                  title: translations.stakingFunds,
                  value: intlFiatValueFormatting(stakedFiat?.value || 0, {
                    locale,
                    currency,
                    fractionDigits: currentUnstakingAmount?.precision,
                  }),
                },
                {
                  title: translations.availableFunds,
                  value: intlFiatValueFormatting(availableAmount || 0, {
                    locale,
                    currency,
                    fractionDigits: currentUnstakingAmount?.precision,
                  }),
                },
                {
                  title: translations.stakeFundsAvailable,
                  value: (
                    <span
                      className={css({
                        color: 'positive.text_primary',
                        fontFamily: 'label.medium_bold',
                        fontSize: 'label.medium_bold',
                        fontWeight: 'label.medium_bold',
                        lineHeight: 'label.medium_bold',
                        letterSpacing: 'label.medium_bold',
                      })}
                    >
                      {unboundedPeriodInDays === 0
                        ? translations.today
                        : fundsAvailableAt}
                    </span>
                  ),
                },
              ]}
            />
          </div>
        </div>
      </div>

      <div
        className={css({
          pb: 'medium',
          marginTop: 'auto',
        })}
      >
        <PoweredByTextWithLogo />

        <ButtonBase
          data-testid="stake-confirm-button"
          visual="primary"
          size="large"
          disabled={isConfirmLoading || isConfirmDone}
          onClick={triggerAcceptUnstake}
          fullWidth
        >
          {translations.unstakeNow}

          {isConfirmLoading && (
            <FilledSpinnerIos
              aria-label="loading icon"
              data-element="loading-icon"
            />
          )}
        </ButtonBase>
      </div>
    </div>
  );
};
